/**
 * GenerationLayoutParameters — the unified packing-parameter controls.
 *
 * Supersedes the old split of "direct single-value inputs" + a separate "Yield
 * search" with its own range rows (the two disagreed and restated the same five
 * parameters). Here every searchable parameter is ONE row that is either:
 *   • Fixed — a single value written straight into the frozen layout via onPatch
 *     (identical behaviour to the former direct inputs), or
 *   • Range — min/max/step that the yield search sweeps.
 * Non-searchable packing choices (orientation, scheme) sit alongside as plain
 * selects. Rotation also carries a 90° nudge. The search space is UI state only
 * and never becomes a solver input, so frozen-exhibit re-validation is
 * unaffected; applying a candidate writes resolved values back and flips those
 * rows to Fixed. Units: ft in the UI, metres in the frozen layout.
 *
 * The three sections (Orientation, Packing, Yield search) share one search
 * state (`useLayoutParameterSearch`) — the yield search sweeps the ranges set on
 * the Packing/Orientation rows. `LayoutParameterSections` renders any subset of
 * those groups against that shared state, so the Studio can surface each as its
 * own command-bar popover while keeping the yield coupling intact.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RotateCw, Search, Wand2 } from 'lucide-react';

import type { BuildGenerationParcelTestFitInput } from '../../../lib/generationImpact/generationLayoutGeometry';
import {
  searchGenerationLayouts,
  type GenerationLayoutSearchCandidate,
  type GenerationLayoutSearchParameterKey,
  type GenerationLayoutSearchResult,
  type GenerationLayoutSearchSpace,
} from '../../../lib/generationImpact/generationLayoutSearch';
import type { GenerationLayoutConfiguration } from '../../../types/generationImpact';

const METRES_PER_FOOT = 0.3048;

type ParamMode = 'fixed' | 'range';

interface RangeState {
  min: number;
  max: number;
  step: number;
}

interface ParameterDefinition {
  key: GenerationLayoutSearchParameterKey;
  /** Visible label. */
  label: string;
  /** Accessible name preserved verbatim for existing tests + a11y. */
  ariaLabel: string;
  /** ft parameters convert to metres at the solver boundary. */
  unit: 'deg' | 'ft' | 'ratio';
  /** Which grouped section the row renders in. */
  group: 'orientation' | 'packing';
  /** Optional disambiguating hint under the row. */
  hint?: string;
  defaults: RangeState;
}

const PARAMETERS: readonly ParameterDefinition[] = [
  { key: 'rotationDeg', label: 'Layout rotation', ariaLabel: 'Layout rotation (degrees)', unit: 'deg', group: 'orientation', defaults: { min: 0, max: 165, step: 15 } },
  { key: 'edgeSetbackM', label: 'Parcel-edge setback', ariaLabel: 'Parcel-edge setback (ft)', unit: 'ft', group: 'packing', defaults: { min: 25, max: 100, step: 25 } },
  { key: 'componentGapM', label: 'Component spacing', ariaLabel: 'Component spacing (ft)', unit: 'ft', group: 'packing', defaults: { min: 50, max: 150, step: 50 } },
  { key: 'generationBlockAspectRatio', label: 'Block width : depth', ariaLabel: 'Generation block width : depth', unit: 'ratio', group: 'packing', defaults: { min: 1, max: 2.5, step: 0.5 } },
  { key: 'rowRoadWidthM', label: 'Inter-row road width', ariaLabel: 'Inter-row road width (ft)', unit: 'ft', group: 'packing', hint: 'Auto-packing corridor between block rows — not the ribbon Road tool.', defaults: { min: 0, max: 120, step: 40 } },
];

/** Read a parameter's live solver value (metres/degrees/ratio) off the layout. */
function layoutValue(key: GenerationLayoutSearchParameterKey, layout: GenerationLayoutConfiguration): number {
  switch (key) {
    case 'rotationDeg': return layout.rotationDeg;
    case 'edgeSetbackM': return layout.edgeSetbackM;
    case 'componentGapM': return layout.componentGapM;
    case 'generationBlockAspectRatio': return layout.generationBlockAspectRatio;
    case 'rowRoadWidthM': return layout.rowRoadWidthM ?? 0;
  }
}

/** Typed patch for a single fixed-value commit (no casts → strict-null clean). */
function fixedPatch(
  key: GenerationLayoutSearchParameterKey,
  solverValue: number,
): Partial<GenerationLayoutConfiguration> {
  switch (key) {
    case 'rotationDeg': return { schemaVersion: 2, rotationDeg: solverValue };
    case 'edgeSetbackM': return { schemaVersion: 2, edgeSetbackM: solverValue };
    case 'componentGapM': return { schemaVersion: 2, componentGapM: solverValue };
    case 'generationBlockAspectRatio': return { schemaVersion: 2, generationBlockAspectRatio: solverValue };
    case 'rowRoadWidthM': return { schemaVersion: 2, rowRoadWidthM: solverValue };
  }
}

function validFixedDisplayValue(definition: ParameterDefinition, value: number): boolean {
  if (!Number.isFinite(value)) return false;
  if (definition.unit === 'ratio') return value >= 0.1 && value <= 10;
  return definition.key === 'rotationDeg' || value >= 0;
}

function toDisplay(definition: ParameterDefinition, solverValue: number): number {
  return definition.unit === 'ft' ? Math.round(solverValue / METRES_PER_FOOT * 10) / 10 : solverValue;
}

function toSolver(definition: ParameterDefinition, displayValue: number): number {
  return definition.unit === 'ft' ? displayValue * METRES_PER_FOOT : displayValue;
}

function formatResolved(key: string, value: number): string {
  const definition = PARAMETERS.find(parameter => parameter.key === key);
  if (!definition) return `${key} ${value}`;
  const display = definition.unit === 'ft'
    ? `${Math.round(value / METRES_PER_FOOT)} ft`
    : definition.unit === 'deg'
      ? `${value}°`
      : `${value}`;
  return `${definition.label} ${display}`;
}

const UNIT_SUFFIX: Record<ParameterDefinition['unit'], string> = { deg: '°', ft: 'ft', ratio: ': 1' };

/** The section groups `LayoutParameterSections` can render. */
export type LayoutParameterGroup = 'orientation' | 'packing' | 'yield';

/**
 * Shared search state for the layout-parameter sections. Instantiate ONCE and
 * pass to every `LayoutParameterSections` that must agree on ranges + results
 * (so the Yield popover sweeps the ranges set on the Packing/Orientation rows).
 */
export interface LayoutParameterSearch {
  modes: Record<GenerationLayoutSearchParameterKey, ParamMode>;
  ranges: Record<GenerationLayoutSearchParameterKey, RangeState>;
  busy: boolean;
  fixedDrafts: Partial<Record<GenerationLayoutSearchParameterKey, string>>;
  rangeDrafts: Record<string, string>;
  result: GenerationLayoutSearchResult | null;
  resultFingerprint: string | null;
  rangeKeys: readonly ParameterDefinition[];
  setMode: (key: GenerationLayoutSearchParameterKey, mode: ParamMode) => void;
  patchRange: (key: GenerationLayoutSearchParameterKey, patch: Partial<RangeState>) => void;
  runSearch: () => void;
  applyCandidate: (candidate: GenerationLayoutSearchCandidate, fingerprint: string) => void;
  setFixedDrafts: React.Dispatch<React.SetStateAction<Partial<Record<GenerationLayoutSearchParameterKey, string>>>>;
  clearFixedDraft: (key: GenerationLayoutSearchParameterKey) => void;
  commitFixedDraft: (definition: ParameterDefinition) => void;
  setRangeDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  commitRangeDraft: (parameter: ParameterDefinition, field: keyof RangeState) => void;
  rangeDraftKey: (key: GenerationLayoutSearchParameterKey, field: keyof RangeState) => string;
}

export function useLayoutParameterSearch(
  base: Omit<BuildGenerationParcelTestFitInput, 'layout'>,
  activeLayout: GenerationLayoutConfiguration,
  readOnly: boolean,
  onPatch: (patch: Partial<GenerationLayoutConfiguration>) => void,
): LayoutParameterSearch {
  const [modes, setModes] = useState<Record<GenerationLayoutSearchParameterKey, ParamMode>>(() => (
    Object.fromEntries(PARAMETERS.map(parameter => [parameter.key, 'fixed'])) as Record<GenerationLayoutSearchParameterKey, ParamMode>
  ));
  const [ranges, setRanges] = useState<Record<GenerationLayoutSearchParameterKey, RangeState>>(() => (
    Object.fromEntries(PARAMETERS.map(parameter => [parameter.key, { ...parameter.defaults }])) as Record<GenerationLayoutSearchParameterKey, RangeState>
  ));
  const [busy, setBusy] = useState(false);
  const [fixedDrafts, setFixedDrafts] = useState<
    Partial<Record<GenerationLayoutSearchParameterKey, string>>
  >({});
  const [rangeDrafts, setRangeDrafts] = useState<Record<string, string>>({});
  const [resultSnapshot, setResultSnapshot] = useState<{
    fingerprint: string;
    result: GenerationLayoutSearchResult;
  } | null>(null);
  const searchRequestRef = useRef(0);
  const searchTimerRef = useRef<number | null>(null);

  const hasExclusions = (activeLayout.exclusions?.zones.length ?? 0) > 0;
  const rangeKeys = useMemo(
    () => PARAMETERS.filter(parameter => modes[parameter.key] === 'range'),
    [modes],
  );

  const setMode = useCallback((key: GenerationLayoutSearchParameterKey, mode: ParamMode) => {
    setModes(current => ({ ...current, [key]: mode }));
    setFixedDrafts(current => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setResultSnapshot(null);
  }, []);

  const patchRange = useCallback((key: GenerationLayoutSearchParameterKey, patch: Partial<RangeState>) => {
    setRanges(current => ({ ...current, [key]: { ...current[key], ...patch } }));
    setResultSnapshot(null);
  }, []);

  const space = useMemo((): GenerationLayoutSearchSpace => Object.fromEntries(
    rangeKeys.map(parameter => {
      const row = ranges[parameter.key];
      return [parameter.key, {
        min: toSolver(parameter, row.min),
        max: toSolver(parameter, row.max),
        step: toSolver(parameter, row.step),
      }];
    }),
  ), [rangeKeys, ranges]);

  // Candidates are valid only for the exact parcel/layout/range snapshot that
  // produced them. JSON is safe here because every solver input is persisted
  // plain data, and it gives deterministic value semantics across rerenders.
  const inputFingerprint = useMemo(
    () => JSON.stringify({ base, activeLayout, space }),
    [activeLayout, base, space],
  );
  const latestFingerprintRef = useRef(inputFingerprint);
  latestFingerprintRef.current = inputFingerprint;
  const result = resultSnapshot?.fingerprint === inputFingerprint
    ? resultSnapshot.result
    : null;

  useEffect(() => {
    searchRequestRef.current += 1;
    if (searchTimerRef.current != null) {
      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setBusy(false);
    setResultSnapshot(null);
  }, [inputFingerprint]);

  useEffect(() => () => {
    searchRequestRef.current += 1;
    if (searchTimerRef.current != null) window.clearTimeout(searchTimerRef.current);
  }, []);

  const runSearch = useCallback(() => {
    if (searchTimerRef.current != null) window.clearTimeout(searchTimerRef.current);
    const requestId = ++searchRequestRef.current;
    const fingerprint = inputFingerprint;
    setBusy(true);
    setResultSnapshot(null);
    // Yield one frame so the busy state paints before the synchronous sweep.
    searchTimerRef.current = window.setTimeout(() => {
      try {
        const nextResult = searchGenerationLayouts({
          base,
          baseLayout: { ...activeLayout, schemaVersion: 2 },
          space,
          // Exclusion-aware fits also run the anchor sweep per candidate;
          // keep the budget smaller so the button stays responsive.
          maxEvaluations: hasExclusions ? 96 : 240,
        });
        if (
          searchRequestRef.current === requestId
          && latestFingerprintRef.current === fingerprint
        ) {
          setResultSnapshot({ fingerprint, result: nextResult });
        }
      } finally {
        if (searchRequestRef.current === requestId) {
          searchTimerRef.current = null;
          setBusy(false);
        }
      }
    }, 30);
  }, [activeLayout, base, hasExclusions, inputFingerprint, space]);

  const applyCandidate = useCallback((
    candidate: GenerationLayoutSearchCandidate,
    fingerprint: string,
  ) => {
    if (fingerprint !== latestFingerprintRef.current) return;
    onPatch({ schemaVersion: 2, ...candidate.resolvedValues });
    // The applied values now live in the layout — show them as Fixed.
    setModes(current => {
      const next = { ...current };
      for (const key of Object.keys(candidate.resolvedValues)) {
        if (key in next) next[key as GenerationLayoutSearchParameterKey] = 'fixed';
      }
      return next;
    });
  }, [onPatch]);

  const clearFixedDraft = useCallback((key: GenerationLayoutSearchParameterKey) => {
    setFixedDrafts(current => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const commitFixedDraft = useCallback((definition: ParameterDefinition) => {
    const raw = fixedDrafts[definition.key];
    if (raw === undefined) return;
    const displayValue = Number(raw.trim());
    if (raw.trim() !== '' && validFixedDisplayValue(definition, displayValue)) {
      onPatch(fixedPatch(definition.key, toSolver(definition, displayValue)));
    }
    clearFixedDraft(definition.key);
  }, [clearFixedDraft, fixedDrafts, onPatch]);

  const rangeDraftKey = useCallback((
    key: GenerationLayoutSearchParameterKey,
    field: keyof RangeState,
  ): string => `${key}:${field}`, []);

  const commitRangeDraft = useCallback((
    parameter: ParameterDefinition,
    field: keyof RangeState,
  ): void => {
    const key = rangeDraftKey(parameter.key, field);
    const raw = rangeDrafts[key];
    if (raw !== undefined) {
      const parsed = Number(raw.trim());
      if (raw.trim() !== '' && Number.isFinite(parsed)) {
        patchRange(parameter.key, { [field]: parsed });
      }
    }
    setRangeDrafts(current => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, [patchRange, rangeDraftKey, rangeDrafts]);

  return {
    modes,
    ranges,
    busy,
    fixedDrafts,
    rangeDrafts,
    result,
    resultFingerprint: resultSnapshot?.fingerprint ?? null,
    rangeKeys,
    setMode,
    patchRange,
    runSearch,
    applyCandidate,
    setFixedDrafts,
    clearFixedDraft,
    commitFixedDraft,
    setRangeDrafts,
    commitRangeDraft,
    rangeDraftKey,
  };
}

export interface LayoutParameterSectionsProps {
  activeLayout: GenerationLayoutConfiguration;
  readOnly: boolean;
  onPatch: (patch: Partial<GenerationLayoutConfiguration>) => void;
  search: LayoutParameterSearch;
  /** Which section groups to render (default: all three). */
  sections?: readonly LayoutParameterGroup[];
  /**
   * True when every component is pinned, so the packer has nothing left to
   * arrange. The packing SCHEME and the yield SEARCH only affect components the
   * packer still positions, so they are disabled with a reason rather than
   * hidden — orientation, parcel and exclusions all still apply to a fully
   * manual layout (pins are stored in the LOCAL frame, so rotation still moves
   * them), and hiding controls that are also the way back to Auto traps users.
   */
  packerInert?: boolean;
}

const ALL_SECTIONS: readonly LayoutParameterGroup[] = ['orientation', 'packing', 'yield'];

/** Renders any subset of the layout-parameter groups against a shared search. */
export const LayoutParameterSections: React.FC<LayoutParameterSectionsProps> = ({
  activeLayout,
  readOnly,
  onPatch,
  search,
  sections = ALL_SECTIONS,
  packerInert = false,
}) => {
  const {
    modes, ranges, busy, fixedDrafts, rangeDrafts, result, resultFingerprint, rangeKeys,
    setMode, patchRange, runSearch, applyCandidate,
    setFixedDrafts, clearFixedDraft, commitFixedDraft, setRangeDrafts, commitRangeDraft, rangeDraftKey,
  } = search;

  const renderRow = (parameter: ParameterDefinition): React.JSX.Element => {
    const mode = modes[parameter.key];
    const range = ranges[parameter.key];
    return (
      <div key={parameter.key} className="border border-border/70 bg-background/40 p-2 rounded-md" data-testid={`generation-parameter-${parameter.key}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">{parameter.label}</span>
          <div className="inline-flex overflow-hidden border border-border rounded-md" role="group" aria-label={`${parameter.label} mode`}>
            {(['fixed', 'range'] as const).map(candidate => (
              <button
                key={candidate}
                type="button"
                disabled={readOnly || busy}
                onClick={() => setMode(parameter.key, candidate)}
                aria-pressed={mode === candidate}
                className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider transition-colors duration-100 ${mode === candidate ? 'bg-primary/15 text-primary' : 'text-text-muted hover:text-text-primary'} disabled:opacity-40`}
                data-testid={`generation-parameter-${parameter.key}-${candidate}`}
              >
                {candidate}
              </button>
            ))}
          </div>
        </div>

        {mode === 'fixed' ? (
          <div className="mt-1.5 flex items-center gap-1.5">
            <input
              type="number"
              aria-label={parameter.ariaLabel}
              value={fixedDrafts[parameter.key]
                ?? toDisplay(parameter, layoutValue(parameter.key, activeLayout))}
              disabled={readOnly}
              step={parameter.unit === 'ratio' ? 0.1 : 1}
              {...(parameter.unit === 'ratio' ? { min: 0.1, max: 10 } : { min: parameter.key === 'rotationDeg' ? undefined : 0 })}
              onChange={(event) => setFixedDrafts(current => ({
                ...current,
                [parameter.key]: event.target.value,
              }))}
              onBlur={() => commitFixedDraft(parameter)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
                if (event.key === 'Escape') clearFixedDraft(parameter.key);
              }}
              className="w-full border border-border bg-background p-1.5 text-xs text-text-primary disabled:opacity-50 rounded-md"
              data-testid={`generation-parameter-${parameter.key}-fixed-input`}
            />
            <span className="shrink-0 text-[10px] text-text-muted">{UNIT_SUFFIX[parameter.unit]}</span>
            {parameter.key === 'rotationDeg' && (
              <button
                type="button"
                disabled={readOnly}
                onClick={() => onPatch({
                  schemaVersion: 2,
                  rotationDeg: (activeLayout.rotationDeg + 90) % 360,
                })}
                title="Rotate the plan 90°"
                aria-label="Rotate the plan 90 degrees"
                className="flex shrink-0 items-center justify-center border border-border p-1.5 text-text-secondary hover:border-primary hover:text-primary disabled:opacity-40 rounded-md"
                data-testid="generation-parameter-rotate-90"
              >
                <RotateCw className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
        ) : (
          <div className="mt-1.5 grid grid-cols-3 gap-1">
            {(['min', 'max', 'step'] as const).map(field => (
              <label key={field} className="text-[9px] uppercase text-text-muted">
                {field}
                <input
                  type="number"
                  aria-label={`${parameter.label} ${field}`}
                  value={rangeDrafts[rangeDraftKey(parameter.key, field)] ?? range[field]}
                  disabled={readOnly || busy}
                  onChange={(event) => setRangeDrafts(current => ({
                    ...current,
                    [rangeDraftKey(parameter.key, field)]: event.target.value,
                  }))}
                  onBlur={() => commitRangeDraft(parameter, field)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                    if (event.key === 'Escape') {
                      const key = rangeDraftKey(parameter.key, field);
                      setRangeDrafts(current => {
                        const next = { ...current };
                        delete next[key];
                        return next;
                      });
                    }
                  }}
                  className="mt-0.5 w-full border border-border bg-background p-1 text-[10px] text-text-primary disabled:opacity-40 rounded-md"
                />
              </label>
            ))}
          </div>
        )}

        {parameter.hint && (
          <p className="mt-1 text-[9px] leading-tight text-text-muted">{parameter.hint}</p>
        )}
      </div>
    );
  };

  const orientationRows = PARAMETERS.filter(parameter => parameter.group === 'orientation');
  const packingRows = PARAMETERS.filter(parameter => parameter.group === 'packing');

  return (
    <div className="grid gap-3" data-testid="generation-layout-parameters">
      {sections.includes('orientation') && (
        <ParameterGroup label="Orientation">
          <label className="text-[10px] uppercase text-text-muted">Packing orientation
            <select
              value={activeLayout.orientation}
              disabled={readOnly}
              onChange={(event) => onPatch({
                schemaVersion: 2,
                orientation: event.target.value as GenerationLayoutConfiguration['orientation'],
              })}
              className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary disabled:opacity-50 rounded-md"
            >
              <option value="auto">Auto-select best orientation</option>
              <option value="blocks-along-width">Blocks along layout width</option>
              <option value="blocks-along-height">Blocks along layout height</option>
            </select>
          </label>
          {orientationRows.map(renderRow)}
        </ParameterGroup>
      )}

      {sections.includes('packing') && (
        <ParameterGroup label="Packing">
          {packerInert && (
            <p className="text-[10px] leading-snug text-text-muted" data-testid="generation-packer-inert-note">
              Every component is pinned — switch Placement to <span className="font-bold">Auto</span> to re-pack.
            </p>
          )}
          <label className={`text-[10px] uppercase text-text-muted ${packerInert ? 'opacity-50' : ''}`}>Packing scheme
            <select
              value={activeLayout.packingScheme ?? 'compact'}
              disabled={readOnly || packerInert}
              onChange={(event) => onPatch({
                schemaVersion: 2,
                packingScheme: event.target.value as GenerationLayoutConfiguration['packingScheme'],
              })}
              className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary disabled:opacity-50 rounded-md"
              data-testid="generation-packing-scheme"
            >
              <option value="compact">Compact</option>
              <option value="road-rows">Road corridors between block rows</option>
              <option value="auto">Auto — prefer road rows when they fit</option>
            </select>
          </label>
          {packingRows.map(renderRow)}
        </ParameterGroup>
      )}

      {sections.includes('yield') && (
        <ParameterGroup label="Yield search">
          <p className="text-[10px] leading-snug text-text-muted">
            <Wand2 className="mr-1 inline h-3 w-3 text-primary" aria-hidden />
            Switch parameters to <strong>Range</strong>; the search packs every combination and ranks
            candidates by blocks contained, exclusion overlap, containment, and green reserve.
          </p>
          <button
            type="button"
            disabled={readOnly || busy || packerInert || rangeKeys.length === 0}
            onClick={runSearch}
            className="flex w-full items-center justify-center gap-1.5 border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary disabled:opacity-40 rounded-md"
            data-testid="generation-layout-search-run"
          >
            <Search className="h-3 w-3" aria-hidden />
            {busy
              ? 'Packing candidates…'
              : packerInert
                ? 'Every component is pinned'
                : rangeKeys.length === 0
                ? 'Switch a parameter to Range'
                : `Run yield search (${rangeKeys.length} ranged)`}
          </button>

          {result?.status === 'invalid' && (
            <p className="border-l-2 border-warning pl-2 text-[10px] text-warning">
              {result.errors.join(' ')}
            </p>
          )}

          {result?.status === 'available' && resultFingerprint && (
            <div className="space-y-1.5" data-testid="generation-layout-search-results">
              <p className="text-[10px] text-text-muted">
                {result.evaluatedCount} candidates packed{result.truncated ? ' (budget reached — coarsen a step to cover the full grid)' : ''}.
              </p>
              {result.candidates.map(candidate => (
                <SearchCandidateCard
                  key={candidate.rank}
                  candidate={candidate}
                  readOnly={readOnly}
                  onApply={() => applyCandidate(candidate, resultFingerprint)}
                />
              ))}
            </div>
          )}
        </ParameterGroup>
      )}
    </div>
  );
};

export interface GenerationLayoutParametersProps {
  base: Omit<BuildGenerationParcelTestFitInput, 'layout'>;
  activeLayout: GenerationLayoutConfiguration;
  readOnly: boolean;
  onPatch: (patch: Partial<GenerationLayoutConfiguration>) => void;
}

/** Convenience wrapper: owns its own search state and renders all sections. */
export const GenerationLayoutParameters: React.FC<GenerationLayoutParametersProps> = ({
  base,
  activeLayout,
  readOnly,
  onPatch,
}) => {
  const search = useLayoutParameterSearch(base, activeLayout, readOnly, onPatch);
  return (
    <LayoutParameterSections
      activeLayout={activeLayout}
      readOnly={readOnly}
      onPatch={onPatch}
      search={search}
    />
  );
};

/** Machined section cluster mirroring the ribbon's RibbonGroup label style. */
const ParameterGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <section className="grid gap-2" aria-label={label}>
    <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted/70">
      {label}
      <span className="grow border-b border-border/60" aria-hidden />
    </p>
    {children}
  </section>
);

function SearchCandidateCard({
  candidate,
  readOnly,
  onApply,
}: {
  candidate: GenerationLayoutSearchCandidate;
  readOnly: boolean;
  onApply: () => void;
}): React.JSX.Element {
  const { testFit, yieldSummary } = candidate;
  const statusTone = testFit.status === 'fits'
    ? 'border-success/50 text-success'
    : testFit.status === 'partial' ? 'border-danger/50 text-danger' : 'border-warning/50 text-warning';
  return (
    <div className="border border-border bg-background p-2 rounded-md" data-testid={`generation-layout-search-candidate-${candidate.rank}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold text-text-primary">#{candidate.rank}</span>
        <span className={`border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${statusTone} rounded-sm`}>
          {testFit.status === 'fits' ? 'Fits' : testFit.status === 'partial' ? 'Fit issue' : 'Unverified'}
        </span>
      </div>
      <p className="mt-1 text-[10px] text-text-secondary">
        {Object.entries(candidate.resolvedValues)
          .map(([key, value]) => formatResolved(key, value as number))
          .join(' · ') || 'Base layout values'}
      </p>
      <p className="mt-0.5 text-[10px] text-text-muted">
        Blocks {yieldSummary.blocksContained}/{yieldSummary.blocksPlaced}
        {testFit.containmentPct != null ? ` · ${testFit.containmentPct.toFixed(1)}% contained` : ''}
        {yieldSummary.greenReservePct != null ? ` · ${yieldSummary.greenReservePct.toFixed(1)}% reserve` : ''}
        {testFit.exclusionOverlapAcres != null && testFit.exclusionOverlapAcres > 0.01
          ? ` · ${testFit.exclusionOverlapAcres.toFixed(1)} ac on exclusions`
          : ''}
      </p>
      <button
        type="button"
        disabled={readOnly}
        onClick={onApply}
        className="mt-1.5 w-full border border-primary/60 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-primary disabled:opacity-40 rounded-md"
      >
        Apply to layout
      </button>
    </div>
  );
}
